import { memo } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from '@heroui/react/tooltip';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Lock } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { NavIcon } from './nav-config';

/**
 * The three shapes a nav row can take. Every rail, drawer and flyout
 * in the shell renders one of these — there is no other nav markup.
 *
 * Styling notes (the "premium" pass):
 *  • ONE active treatment: a tinted surface plus an inset accent bar.
 *    The old shell stacked `bg-primary-50` + `ring-1` + `shadow-sm`
 *    + bolder weight, which read as noisy at 15 items.
 *  • No `scale-110` icon hover. Icons shift tone, not size — jitter
 *    on hover is the single most "unfinished" tell in a sidebar.
 *  • Every colour is a semantic token from color.css. No raw hex,
 *    no palette steps.
 */

const ROW_BASE = cn(
  'group relative flex w-full items-center rounded-md',
  'text-body font-medium no-underline hover:no-underline',
  'transition-colors duration-(--shell-motion-fast)',
  'outline-none focus-visible:ring-2 focus-visible:ring-primary/60',
  'focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--surface-1)]',
);

const ROW_IDLE =
  'text-secondary-500 hover:bg-surface-2 hover:text-secondary-800';
const ROW_ACTIVE = 'bg-brand-soft text-brand-text font-semibold';

/** Inset accent bar marking the active row. Mirrors automatically in RTL. */
function AccentBar() {
  return (
    <span
      aria-hidden
      className="absolute inset-y-1 start-0 w-0.5 rounded-full bg-brand"
    />
  );
}

export function NavCount({
  value,
  tone = 'primary',
}: {
  value: number;
  tone?: 'primary' | 'accent';
}) {
  if (!value) return null;
  return (
    <span
      className={cn(
        'ms-auto inline-flex min-w-5 items-center justify-center rounded-full',
        'px-1.5 py-0.5 text-micro font-semibold tabular-nums',
        tone === 'accent'
          ? 'bg-tertiary-500 text-[var(--color-accent-on)]'
          : 'bg-brand text-brand-on',
      )}
    >
      {value > 99 ? '99+' : value}
    </span>
  );
}

/**
 * Marks a destination the plan does not include. Quiet on purpose: the entry
 * still works (it opens the upgrade panel), it just says what it is.
 */
export function NavLock({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        'ms-auto inline-flex shrink-0 text-secondary-400',
        className,
      )}
      title={t('planFeatures.navLocked')}
    >
      <Lock aria-hidden className="size-3.5" />
      <span className="sr-only">{t('planFeatures.navLocked')}</span>
    </span>
  );
}

/**
 * Wraps a collapsed-rail control so its label is still reachable.
 *
 * `Tooltip.Trigger` renders a real `<div>` wrapper (it is not a
 * `Slot`), which becomes the flex child in place of the row itself.
 * Without `w-full` that wrapper shrink-wraps its content, the row
 * loses its full width, and the icons bunch toward the start edge
 * instead of centring in the 72px rail.
 */
export function RailTooltip({
  label,
  enabled,
  children,
}: {
  label: string;
  enabled: boolean;
  children: React.ReactNode;
}) {
  if (!enabled) return <>{children}</>;
  return (
    <Tooltip.Root delay={200}>
      <Tooltip.Trigger className="block w-full">{children}</Tooltip.Trigger>
      <Tooltip.Content placement="end" className="text-small">
        {label}
      </Tooltip.Content>
    </Tooltip.Root>
  );
}

export interface NavLinkRowProps {
  to: string;
  icon: NavIcon;
  label: string;
  isActive: boolean;
  collapsed?: boolean;
  badge?: number;
  badgeTone?: 'primary' | 'accent';
  /** Sub-items sit in an indented column and use a lighter type scale. */
  depth?: 0 | 1;
  /** The plan does not include it; see NavLock. */
  locked?: boolean;
  onNavigate?: () => void;
}

export const NavLinkRow = memo(function NavLinkRow({
  to,
  icon: Icon,
  label,
  isActive,
  collapsed = false,
  badge = 0,
  badgeTone = 'primary',
  depth = 0,
  locked = false,
  onNavigate,
}: NavLinkRowProps) {
  const row = (
    // A plain Link, not NavLink: NavLink adds its own aria-current="page"
    // whenever the URL starts with `to`, so a parent such as "All settings"
    // announced itself as the current page on every page beneath it.
    <Link
      to={to}
      onClick={onNavigate}
      // aria-current is what actually conveys "you are here" to a
      // screen reader; the old shell set it on 2 of ~60 links.
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        ROW_BASE,
        isActive ? ROW_ACTIVE : ROW_IDLE,
        collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5',
        depth === 1 && !collapsed && 'gap-2.5 px-2.5 py-2 text-small',
      )}
    >
      {isActive && depth === 0 && <AccentBar />}
      <Icon
        aria-hidden
        className={cn('shrink-0', depth === 1 ? 'size-4' : 'size-icon')}
      />
      {!collapsed && <span className="truncate">{label}</span>}
      {!collapsed &&
        (locked ? <NavLock /> : <NavCount value={badge} tone={badgeTone} />)}
      {collapsed && badge > 0 && (
        <span
          aria-hidden
          className="absolute end-2 top-1.5 size-2 rounded-full bg-tertiary-500"
        />
      )}
    </Link>
  );

  return (
    <RailTooltip label={label} enabled={collapsed}>
      {row}
    </RailTooltip>
  );
});

export interface NavGroupRowProps {
  icon: NavIcon;
  label: string;
  isActive: boolean;
  isOpen: boolean;
  collapsed?: boolean;
  badge?: number;
  /** Every destination inside is outside the plan. */
  locked?: boolean;
  controls: string;
  onToggle: () => void;
}

export const NavGroupRow = memo(function NavGroupRow({
  icon: Icon,
  label,
  isActive,
  isOpen,
  collapsed = false,
  badge = 0,
  locked = false,
  controls,
  onToggle,
}: NavGroupRowProps) {
  const row = (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={collapsed ? undefined : isOpen}
      aria-controls={collapsed ? undefined : controls}
      aria-label={collapsed ? label : undefined}
      className={cn(
        ROW_BASE,
        isActive ? ROW_ACTIVE : ROW_IDLE,
        collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5',
      )}
    >
      {isActive && <AccentBar />}
      <Icon aria-hidden className="size-icon shrink-0" />
      {!collapsed && (
        <>
          <span className="truncate text-start">{label}</span>
          {/* Badge hides while open — the count is visible on the child. */}
          {!isOpen && <NavCount value={badge} tone="accent" />}
          {locked && <NavLock />}
          <ChevronDown
            aria-hidden
            className={cn(
              !locked && 'ms-auto',
              'size-4 shrink-0 text-secondary-400',
              'transition-transform duration-(--shell-motion-fast)',
              isOpen && 'rotate-180',
            )}
          />
        </>
      )}
      {collapsed && badge > 0 && (
        <span
          aria-hidden
          className="absolute end-2 top-1.5 size-2 rounded-full bg-tertiary-500"
        />
      )}
    </button>
  );

  return (
    <RailTooltip label={label} enabled={collapsed}>
      {row}
    </RailTooltip>
  );
});

/** Uppercase eyebrow separating rail sections. */
export function NavSectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-3 pb-1 pt-4 text-micro font-semibold uppercase tracking-wider text-secondary-400 first:pt-1">
      {children}
    </p>
  );
}

/** Hairline used in place of a section label on the collapsed rail. */
export function NavSectionRule() {
  return (
    <div
      role="presentation"
      className="mx-auto my-2 h-px w-6 bg-border-default"
    />
  );
}

/** Sub-heading inside a long group's child list. */
export function NavSubheadingRow({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-2.5 pb-0.5 pt-2.5 text-micro font-semibold uppercase tracking-wider text-secondary-400 first:pt-0.5">
      {children}
    </p>
  );
}
